import React from "react";
import {
	Button,
	ClassNameProps,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	toGrammaticalNumber,
	useSync,
} from "@hex-insights/core";
import { SlimEmptyView } from "../../../../../Components/Empty";
import { Tile } from "../../../../../Components/Tile";
import {
	Student,
	StudentFilterInput,
	StudentNameAndImageFragment,
	StudentNameDescriptionAndImageFragment,
	truncateDisplayItems,
	useDynamicTruncation,
	useStudentNamesAndImagesQuery,
} from "../../../../../Utilities";
import { StudentLink } from "../../../../Links";
import { StudentIcon } from "../../Icon";
import {
	ControlledStudentsListModal,
	ControlledStudentsListModalProps,
	StudentsListModal,
	StudentsListModalProps,
} from "../../ListModal";

export type ControlledStudentsRowTileProps = {
	filters?: StudentFilterInput;
	skip?: boolean;
} & Pick<ControlledStudentsListModalProps, "groupBy"> &
	Partial<ClassNameProps & StyleProps>;

export function ControlledStudentsRowTile({
	filters,
	skip,
	groupBy,
	className,
	style,
}: ControlledStudentsRowTileProps) {
	const { loading, data } = useStudentNamesAndImagesQuery({
		variables: {
			filters,
			pagination: { first: 30 },
		},
		skip,
	});
	const students = React.useMemo(() => data?.studentConnection.edges.map((e) => e.node), [data]);

	const ListModal = React.useCallback(
		({ ifRef, onClose }: Pick<ModalProps, "ifRef" | "onClose">) => (
			<ControlledStudentsListModal filters={filters} groupBy={groupBy} ifRef={ifRef} onClose={onClose} />
		),
		[filters, groupBy],
	);

	return (
		<BaseStudentsRowTile
			isLoading={loading}
			students={students}
			totalCount={data?.studentConnection.totalCount}
			ListModal={ListModal}
			className={className}
			style={style}
		/>
	);
}

export type StudentsRowTileProps = {
	isLoading?: boolean;
	students: (Pick<Student, "id"> & StudentNameDescriptionAndImageFragment)[] | null | undefined;
} & Pick<StudentsListModalProps, "groupBy"> &
	Partial<ClassNameProps & StyleProps>;

export function StudentsRowTile({ isLoading, students, groupBy, className, style }: StudentsRowTileProps) {
	const ListModal = React.useCallback(
		({ ifRef, onClose }: Pick<ModalProps, "ifRef" | "onClose">) => (
			<StudentsListModal
				isLoading={isLoading}
				students={students ?? []}
				groupBy={groupBy}
				ifRef={ifRef}
				onClose={onClose}
			/>
		),
		[isLoading, students, groupBy],
	);

	return (
		<BaseStudentsRowTile
			isLoading={isLoading}
			students={students}
			ListModal={ListModal}
			className={className}
			style={style}
		/>
	);
}

type BaseStudentsRowTileProps = {
	isLoading?: boolean;
	students: (Pick<Student, "id"> & StudentNameAndImageFragment)[] | null | undefined;
	totalCount?: number;
	ListModal: (props: Pick<ModalProps, "ifRef" | "onClose">) => JSX.Element | null;
} & Partial<ClassNameProps & StyleProps>;

function BaseStudentsRowTile({
	isLoading = false,
	students,
	totalCount,
	ListModal,
	className,
	style,
}: BaseStudentsRowTileProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const ref = React.useRef<HTMLDivElement | null>(null);
	const { synchronize } = useSync();
	React.useEffect(() => {
		synchronize();
	}, [synchronize, students]);
	const gapSize = 12;
	const { maxDisplayed } = useDynamicTruncation(
		ref,
		(container) => container?.firstChild?.firstChild as HTMLElement | null | undefined,
		{ gapSize, syncID: !!students && students.length > 0 },
	);
	const { displayedItems } = truncateDisplayItems(students ?? [], maxDisplayed, { includeLast: true });

	const numStudents = totalCount ?? students?.length ?? 0;

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Heading.H2 noMargin>{toGrammaticalNumber("Student", numStudents, numStudents > 0)}</Heading.H2>

						<If condition={numStudents > 0}>
							<Button variant="link" size="small" onClick={openModal}>
								View All
							</Button>
						</If>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<Conditional>
						<If condition={isLoading && !students}>Loading...</If>
						<If condition={!!students && students.length === 0}>
							<SlimEmptyView>No students listed</SlimEmptyView>
						</If>
						<Else>
							<div ref={ref} style={{ width: "100%", overflowX: "hidden" }}>
								<Row justify="spaced-start" horizontalSpacing={`${gapSize}px`} style={{ width: "100%" }}>
									{displayedItems.map((student) => (
										<StudentLink key={student.id} instance={student} className="link--no-text-decoration">
											<StudentIcon student={student} size={48} />
										</StudentLink>
									))}
								</Row>
							</div>
						</Else>
					</Conditional>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<ListModal onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
